/* eslint-disable react/jsx-no-bind */
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { makeStyles } from 'tss-react/mui';
import { logout, openLoginModal, setAuthenticated, setUserInfo } from '../../../auth/actions';
import { authApi } from '../../../auth/api';
import LoginModal from '../../../auth/components/LoginModal.web';
import { ensureDeviceId } from '../../../auth/functions';
import { isNameReadOnly } from '../../../base/config/functions.web';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconArrowUp, IconDotsVertical, IconPhoneRinging, IconVolumeOff } from '../../../base/icons/svg';
import { isVideoMutedByUser } from '../../../base/media/functions';
import { getLocalParticipant } from '../../../base/participants/functions';
import Popover from '../../../base/popover/components/Popover.web';
import ActionButton from '../../../base/premeeting/components/web/ActionButton';
import Preview from '../../../base/premeeting/components/web/Preview';
import { updateSettings } from '../../../base/settings/actions';
import { getDisplayName } from '../../../base/settings/functions.web';
import { getLocalJitsiAudioTrack, getLocalJitsiVideoTrack } from '../../../base/tracks/functions.web';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import isInsecureRoomName from '../../../base/util/isInsecureRoomName';
import { showWarningNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { isUnsafeRoomWarningEnabled } from '../../../prejoin/functions.any';
import { setRoomStartTime } from '../../../room-time-limit/actions';
import { openSettingsDialog } from '../../../settings/actions.web';
import { SETTINGS_TABS } from '../../../settings/constants';
import AudioSettingsButton from '../../../toolbox/components/web/AudioSettingsButton';
import VideoSettingsButton from '../../../toolbox/components/web/VideoSettingsButton';
import { joinConference as joinConferenceAction, joinConferenceWithoutAudio as joinConferenceWithoutAudioAction, setJoinByPhoneDialogVisiblity as setJoinByPhoneDialogVisiblityAction } from '../../actions.web';
import { isDisplayNameRequired, isJoinByPhoneButtonVisible, isJoinByPhoneDialogVisible, isPrejoinDisplayNameVisible } from '../../functions.any';
import logger from '../../logger';
import JoinByPhoneDialog from './dialogs/JoinByPhoneDialog';
import PrejoinAudioIndicator from './PrejoinAudioIndicator';
const useStyles = makeStyles()(() => ({
    container: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100%",
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 252,
        backgroundColor: "white",
        overflow: "hidden",
        direction: "ltr",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        "@media (max-width: 768px)": {
            display: "none !important",
        },
    },
    logo: {
        height: "40px",
        width: "auto",
    },
    logoAnimation: {
        width: "10rem",
        height: "auto",
    },
    // Mobile logo - shown only on mobile devices
    mobileLogo: {
        display: "none",
        position: "absolute",
        top: "30px",
        left: "16px",
        height: "28px",
        width: "auto",
        zIndex: 15,
        "@media (max-width: 768px)": {
            display: "block",
        },
    },
    settingsButton: {
        background: "transparent",
        border: "none",
        cursor: "pointer",
        padding: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        transition: "background 0.2s",
        "&:hover": {
            background: "rgba(0, 0, 0, 0.05)",
        },
        "& svg": {
            fill: "#666",
        },
    },
    userMenu: {
        position: "relative",
    },
    userBtn: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 12px",
        borderRadius: "8px",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        transition: "background 0.2s",
    },
    userAvatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        background: "#0376DA",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.875rem",
        fontWeight: 600,
    },
    userInfo: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
    },
    userName: {
        fontSize: "0.875rem",
        fontWeight: 600,
        color: "#1a1a1a",
    },
    userPhone: {
        fontSize: "0.75rem",
        color: "#666",
    },
    arrowIcon: {
        transition: "transform 0.2s",
    },
    arrowIconActive: {
        transform: "rotate(180deg)",
    },
    dropdown: {
        position: "absolute",
        top: "100%",
        right: 0,
        marginTop: "8px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        minWidth: "180px",
        zIndex: 100,
        overflow: "hidden",
    },
    dropdownItem: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 16px",
        width: "100%",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontSize: "0.875rem",
        color: "#1a1a1a",
        transition: "background 0.2s",
        "&:hover": {
            background: "rgba(0, 0, 0, 0.05)",
        },
    },
    dropdownItemLogout: {
        color: "#dc3545",
        "&:hover": {
            background: "rgba(220, 53, 69, 0.1)",
        },
    },
    mainContent: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: "80px",
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        flex: 1,
        padding: "60px 24px 24px",
        "@media (max-width: 768px)": {
            flexDirection: "column",
            gap: "0",
            padding: "60px 0 16px",
            justifyContent: "flex-start",
            overflow: "hidden",
        },
    },
    leftPanel: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexShrink: 0,
        "@media (max-width: 768px)": {
            width: "100%",
            order: 1,
            position: "relative",
        },
    },
    videoWrapper: {
        width: "740px",
        height: "417px",
        borderRadius: "15px",
        overflow: "hidden",
        position: "relative",
        // Gradient overlays
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "150px",
            background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 44%)",
            zIndex: 5,
            pointerEvents: "none",
        },
        "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "150px",
            background: "linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 55%)",
            zIndex: 5,
            pointerEvents: "none",
        },
        "@media (max-width: 768px)": {
            height: "calc(100vh - 200px)",
            borderRadius: "12px",
            marginTop: "0",
            width: "95%",
        },
    },
    rightPanel: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        placeItems: "center",
        width: "350px",
        textAlign: "center",
        height: "100%",
        "@media (max-width: 768px)": {
            display: "none",
        },
    },
    title: {
        fontSize: "1.25rem",
        fontWeight: 600,
        marginBottom: "16px",
        "@media (max-width: 768px)": {
            display: "none",
        },
    },
    joinButtonWrapper: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        "@media (max-width: 768px)": {
            flexDirection: "row-reverse",
            gap: "10px",
            justifyContent: "center",
            position: "absolute",
            bottom: "24px",
            left: "16px",
            right: "16px",
            width: "auto",
        },
    },
    cancelButton: {
        width: "100px",
        backgroundColor: "#fff",
        color: "#dc3545",
        padding: "12px 24px",
        border: "none",
        borderRadius: "9999px",
        cursor: "pointer",
        fontSize: "1rem",
        fontWeight: 600,
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: "rgba(220, 53, 69, 0.15)",
            borderColor: "rgba(220, 53, 69, 0.5)",
        },
        "@media (max-width: 768px)": {
            fontSize: "0.75rem",
            padding: "10px 20px",
            width: "auto",
            flex: "0 0 auto",
            backgroundColor: "rgba(220, 53, 69, 0.1)",
        },
    },
    dropdownButtons: {
        width: "300px",
        padding: "8px 0",
        backgroundColor: "#333",
        borderRadius: "8px",
        "@media (max-width: 768px)": {
            width: "100%",
            maxWidth: "300px",
        },
    },
    spinner: {
        width: "22px",
        height: "22px",
        border: "3px solid rgba(255,255,255,0.3)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
    },
    // Skeleton loading styles
    skeletonBase: {
        background: "#e8e8e8",
        borderRadius: "8px",
    },
    skeletonHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        "@media (max-width: 768px)": {
            display: "none !important",
        },
    },
    skeletonLogo: {
        width: "120px",
        height: "40px",
        background: "linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "8px",
    },
    skeletonUserInfo: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    skeletonUserText: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "4px",
    },
    skeletonUserName: {
        width: "80px",
        height: "14px",
        background: "linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "4px",
    },
    skeletonUserPhone: {
        width: "100px",
        height: "12px",
        background: "linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "4px",
    },
    skeletonAvatar: {
        width: "36px",
        height: "36px",
        background: "linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "50%",
    },
    skeletonMainContent: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: "80px",
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        flex: 1,
        padding: "60px 24px 24px",
        "@media (max-width: 768px)": {
            flexDirection: "column",
            gap: "16px",
            padding: "60px 0 16px",
            justifyContent: "flex-start",
        },
    },
    skeletonLeftPanel: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexShrink: 0,
        "@media (max-width: 768px)": {
            width: "100%",
            order: 1,
            position: "relative",
        },
    },
    skeletonVideoWrapper: {
        width: "740px",
        height: "417px",
        background: "linear-gradient(90deg, #d0d0d0 25%, #e0e0e0 50%, #d0d0d0 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "15px",
        position: "relative",
        overflow: "hidden",
        "@media (max-width: 768px)": {
            height: "75vh",
            borderRadius: "12px",
            width: "95%",
        },
    },
    skeletonToolbox: {
        position: "absolute",
        bottom: "16px",
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 16px",
    },
    skeletonToolboxContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    skeletonToolboxCenter: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    skeletonToolboxButton: {
        width: "48px",
        height: "48px",
        background: "linear-gradient(90deg, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "50%",
    },
    skeletonToolboxButtonRight: {
        width: "56px",
        height: "56px",
        background: "linear-gradient(90deg, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "50%",
    },
    skeletonUsername: {
        position: "absolute",
        bottom: "70px",
        left: "16px",
        width: "100px",
        height: "20px",
        background: "linear-gradient(90deg, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.2) 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "4px",
    },
    skeletonRightPanel: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "350px",
        textAlign: "center",
        gap: "16px",
        "@media (max-width: 768px)": {
            display: "none",
        },
    },
    skeletonTitle: {
        width: "200px",
        height: "24px",
        background: "linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "6px",
        marginBottom: "8px",
    },
    skeletonJoinButton: {
        width: "100%",
        height: "48px",
        backgroundColor: "#0056b998",
        backgroundSize: "200% 100%",
        borderRadius: "9999px",
    },
    skeletonCancelButton: {
        width: "100px",
        height: "40px",
        background: "linear-gradient(90deg, #ffe0e0 25%, #fff0f0 50%, #ffe0e0 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "9999px",
        marginTop: "8px",
    },
    skeletonMobileButtons: {
        display: "none",
        "@media (max-width: 768px)": {
            display: "flex",
            width: "100%",
            flexDirection: "row-reverse",
            gap: "10px",
            marginTop: "16px",
            justifyContent: "center",
            padding: "0 16px",
        },
    },
    skeletonMobileJoinButton: {
        flex: "1 1 auto",
        maxWidth: "200px",
        height: "44px",
        background: "linear-gradient(90deg, #0056b3 25%, #0070e0 50%, #0056b3 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "9999px",
    },
    skeletonMobileCancelButton: {
        width: "60px",
        height: "44px",
        background: "linear-gradient(90deg, #ffe0e0 25%, #fff0f0 50%, #ffe0e0 75%)",
        backgroundSize: "200% 100%",
        borderRadius: "9999px",
    },
    // Custom toolbox styles for prejoin
    prejoinToolbox: {
        position: "absolute",
        bottom: "16px",
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 16px",
        zIndex: 10,
    },
    toolboxContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    toolboxLeft: {
        display: "flex",
        alignItems: "center",
        borderRadius: "9999px",
    },
    toolboxCenter: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        borderRadius: "9999px",
        // Override toolbox-icon styles for circular buttons with transparent background
        "& .settings-button-container .toolbox-icon": {
            borderRadius: "9999px !important",
            background: "rgba(255, 255, 255, 0.15) !important",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            width: "48px !important",
            height: "48px !important",
            "&:hover": {
                background: "rgba(255, 255, 255, 0.25) !important",
            },
            "&.toggled": {
                background: "rgba(220, 53, 69, 0.8) !important",
                borderColor: "rgba(220, 53, 69, 0.5)",
                "&:hover": {
                    background: "rgba(220, 53, 69, 0.9) !important",
                },
            },
        },
        // Override toolbox-icon directly too
        "& .toolbox-icon": {
            borderRadius: "9999px !important",
            background: "rgba(255, 255, 255, 0.15) !important",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            "&:hover": {
                background: "rgba(255, 255, 255, 0.25) !important",
            },
            "&.toggled": {
                background: "rgba(220, 53, 69, 0.8) !important",
            },
        },
    },
    toolboxRight: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    previewSettingsButton: {
        position: "absolute",
        top: "8px",
        right: "6px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        padding: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        zIndex: 10,
        "& svg": {
            fill: "#fff",
        },
        "@media (max-width: 768px)": {
            top: "12px",
            right: "12px",
        },
    },
    toolboxButton: {
        width: "56px",
        height: "56px",
        borderRadius: "50%",
        background: "transparent",
        backdropFilter: "blur(8px)",
        border: "1px solid white",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.5s",
        "&:hover": {
            background: "rgba(255, 255, 255, 0.25)",
            borderColor: "rgba(255, 255, 255, 0.3)",
        },
        "& svg": {
            fill: "#fff",
        },
        "@media (max-width: 768px)": {
            width: "44px",
            height: "44px",
            "& svg": {
                width: "20px",
                height: "20px",
            },
        },
    },
    // Mobile buttons wrapper - shows on mobile only
    mobileButtonsWrapper: {
        display: "none",
        "@media (max-width: 768px)": {
            display: "flex",
            width: "100%",
            flexDirection: "row-reverse",
            gap: "10px",
            marginTop: "12px",
            justifyContent: "center",
            flexShrink: 0,
        },
    },
    mobileJoinButton: {
        flex: "1 1 auto",
        maxWidth: "200px",
        padding: "10px 6px",
    },
    mobileCancelButton: {
        background: "transparent",
        color: "red",
        padding: "6px 6px",
        border: "none",
        borderRadius: "9999px",
        cursor: "pointer",
        fontSize: "0.7rem",
        fontWeight: 400,
        flex: "0 0 auto",
    },
    // Knocking/Waiting state styles
    knockingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 1)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 20,
        borderRadius: "15px",
        "@media (max-width: 768px)": {
            borderRadius: "12px",
        },
    },
    knockingSpinner: {
        width: "48px",
        height: "48px",
        border: "4px solid rgba(255, 255, 255, 0.3)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "16px",
    },
    knockingText: {
        color: "#fff",
        fontSize: "1rem",
        fontWeight: 500,
        textAlign: "center",
        padding: "0 16px",
    },
    "@keyframes spin": {
        "0%": {
            transform: "rotate(0deg)",
        },
        "100%": {
            transform: "rotate(360deg)",
        },
    },
    autoJoinOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
    },
    autoJoinSpinner: {
        width: "56px",
        height: "56px",
        border: "4px solid rgba(255, 255, 255, 0.2)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "32px",
    },
    autoJoinText: {
        color: "#fff",
        fontSize: "1.25rem",
        fontWeight: 500,
        textAlign: "center",
        lineHeight: 2,
        direction: "ltr",
        '@media (max-width: 768px)': {
            fontSize: '0.875rem',
        },
    },
}));
const Prejoin = ({ audioTrack, hasJoinByPhoneButton, isDisplayNameVisible, isKnocking, joinConference, joinConferenceWithoutAudio, joiningInProgress, name, prejoinConfig, readOnlyName, setJoinByPhoneDialogVisiblity, showCameraPreview, showDialog, showErrorOnJoin, showUnsafeRoomWarning, unsafeRoomConsent, updateSettings: dispatchUpdateSettings, videoTrack, }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showJoinByPhoneButtons, setShowJoinByPhoneButtons] = useState(false);
    const [userDisplayName, setUserDisplayName] = useState(name || "");
    const [isAuthChecking, setIsAuthChecking] = useState(true);
    const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isJoining, setIsJoining] = useState(false);
    const dropdownRef = useRef(null);
    const [autoJoining, setAutoJoining] = useState(() => {
        return localStorage.getItem("newMeetingAutoJoin") === "true";
    });
    const [isNewMeeting, setIsNewMeeting] = useState(false);
    const userInfo = useSelector((state) => state["features/auth"]?.userInfo);
    const showDisplayNameField = useMemo(() => isDisplayNameVisible && !readOnlyName, [isDisplayNameVisible, readOnlyName]);
    const showErrorOnField = useMemo(() => showDisplayNameField && showErrorOnJoin, [showDisplayNameField, showErrorOnJoin]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        if (dropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);
    useEffect(() => {
        const checkAuth = async () => {
            const token = Cookies.get("accessToken");
            if (!token) {
                dispatch(showWarningNotification({
                    titleKey: "auth.loginRequired",
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                dispatch(openLoginModal(window.location.pathname.slice(1)));
                setIsAuthChecking(false);
                setIsUserAuthenticated(false);
                return;
            }
            // First, try to get user info from cookie
            const cachedUserInfo = authApi.getUserInfo();
            if (cachedUserInfo) {
                // Use cached user info immediately
                dispatch(setUserInfo(cachedUserInfo));
                dispatch(setAuthenticated(true));
                setUserDisplayName(cachedUserInfo.username || cachedUserInfo.phone);
                dispatchUpdateSettings({ displayName: cachedUserInfo.username || cachedUserInfo.phone });
                setIsUserAuthenticated(true);
                setIsAuthChecking(false);
                return;
            }
            // If no cached info, fetch from API
            try {
                const me = await authApi.getMe();
                dispatch(setUserInfo(me));
                dispatch(setAuthenticated(true));
                setUserDisplayName(me.username || me.phone);
                dispatchUpdateSettings({ displayName: me.username || me.phone });
                setIsUserAuthenticated(true);
                // Save to cookie for next time
                authApi.saveUserInfo(me);
            }
            catch (error) {
                // Don't clear tokens on network error - user might be temporarily disconnected
                // Only show warning, keep authentication state
                console.warn('Failed to fetch user info, but keeping auth state:', error);
                // Check if token exists - if yes, assume user is still authenticated
                if (authApi.isAuthenticated()) {
                    setIsUserAuthenticated(true);
                }
                else {
                    // Only logout if no token exists
                    dispatch(showWarningNotification({
                        titleKey: "auth.loginRequired",
                    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
                    dispatch(openLoginModal(window.location.pathname.slice(1)));
                    setIsUserAuthenticated(false);
                }
            }
            finally {
                setIsAuthChecking(false);
            }
        };
        checkAuth();
    }, [dispatch, dispatchUpdateSettings]);
    // Auto-join for new meeting (skip prejoin)
    useEffect(() => {
        const shouldAutoJoin = localStorage.getItem("newMeetingAutoJoin");
        const wasInConference = localStorage.getItem("wasInConference");
        const shouldAutoRejoin = localStorage.getItem("shouldAutoRejoin");
        const lastRoomAutoJoin = localStorage.getItem("lastRoomAutoJoin");
        if (shouldAutoJoin && isUserAuthenticated && !isAuthChecking) {
            // Don't remove the flag here - let lobby middleware check it first
            // localStorage.removeItem("newMeetingAutoJoin");
            setAutoJoining(true);
            setIsNewMeeting(true); // This is a new meeting creation
            const roomName = window.location.pathname.slice(1).toLowerCase();
            const deviceId = ensureDeviceId();
            authApi
                .enterRoom(roomName, deviceId)
                .then((response) => {
                console.log('🔑 [PREJOIN AUTO-JOIN] enterRoom API response:', response);
                console.log('🔑 [PREJOIN AUTO-JOIN] response type:', typeof response);
                console.log('🔑 [PREJOIN AUTO-JOIN] response.roomStartTime:', response?.roomStartTime);
                console.log('🔑 [PREJOIN AUTO-JOIN] response.roomStartTime type:', typeof response?.roomStartTime);
                if (response?.roomStartTime) {
                    console.log('✅ [PREJOIN AUTO-JOIN] roomStartTime received:', response.roomStartTime);
                    dispatch(setRoomStartTime(response.roomStartTime));
                }
                else {
                    console.error('❌ [PREJOIN AUTO-JOIN] No roomStartTime in response!');
                }
                joinConference();
            })
                .catch((error) => {
                logger.error("Auto-join failed:", error);
                setAutoJoining(false);
                setIsNewMeeting(false);
            });
        }
        else if (lastRoomAutoJoin && isUserAuthenticated && !isAuthChecking) {
            // User clicked "last room" button - auto-join to last active room
            console.log('🔄 [PREJOIN LAST-ROOM] Auto-joining to last active room...');
            localStorage.removeItem("lastRoomAutoJoin");
            setAutoJoining(true);
            setIsNewMeeting(false);
            const roomName = window.location.pathname.slice(1).toLowerCase();
            const deviceId = ensureDeviceId();
            authApi
                .enterRoom(roomName, deviceId)
                .then((response) => {
                if (response?.roomStartTime) {
                    dispatch(setRoomStartTime(response.roomStartTime));
                }
                joinConference();
            })
                .catch((error) => {
                logger.error("Last room auto-join failed:", error);
                setAutoJoining(false);
                setIsNewMeeting(false);
            });
        }
        else if ((wasInConference || shouldAutoRejoin) && isUserAuthenticated && !isAuthChecking) {
            // Reconnect after connection loss — session still active on backend, skip enterRoom
            console.log('🔄 [PREJOIN AUTO-REJOIN] Rejoining without enterRoom API call...');
            localStorage.removeItem("wasInConference");
            localStorage.removeItem("shouldAutoRejoin");
            setAutoJoining(true);
            setIsNewMeeting(false);
            const cachedStartTime = localStorage.getItem('didar_roomStartTime');
            if (cachedStartTime) {
                dispatch(setRoomStartTime(cachedStartTime));
            }
            joinConference();
        }
    }, [isUserAuthenticated, isAuthChecking, dispatch, joinConference]);
    const handleDropdownToggle = useCallback(() => {
        setDropdownOpen((prev) => !prev);
    }, []);
    const handleSettingsClick = useCallback(() => {
        dispatch(openSettingsDialog(SETTINGS_TABS.AUDIO));
        setDropdownOpen(false);
    }, [dispatch]);
    const handleLogoutClick = useCallback(async () => {
        await authApi.logout();
        dispatch(logout());
        setDropdownOpen(false);
    }, [dispatch]);
    const handleBackgroundClick = useCallback(() => {
        dispatch(openSettingsDialog(SETTINGS_TABS.VIRTUAL_BACKGROUND));
    }, [dispatch]);
    const handleSettingsButtonClick = useCallback(() => {
        dispatch(openSettingsDialog(SETTINGS_TABS.AUDIO));
    }, [dispatch]);
    const onJoinButtonClick = async () => {
        if (showErrorOnJoin || isJoining) {
            return;
        }
        //@ts-ignore
        logger.info("Prejoin join button clicked.");
        setIsJoining(true);
        const roomName = window.location.pathname.slice(1).toLowerCase();
        const deviceId = ensureDeviceId();
        try {
            // First check if room exists
            await authApi.checkRoomName(roomName);
            // Call exitRoom first to cleanup any existing session with this deviceId
            // This prevents duplicate participants when rejoining after connection loss
            // COMMENTED OUT: Testing duplicate participant issue
            // try {
            //     await authApi.exitRoom(roomName, deviceId);
            //     console.log('✅ [PREJOIN] exitRoom called before enterRoom to cleanup old session');
            // } catch (exitError) {
            //     // Ignore error if no session exists - this is expected on first join
            //     console.log('ℹ️ [PREJOIN] exitRoom before enterRoom (expected if no previous session):', exitError);
            // }
            // If we get here, status is 200 and room exists, proceed with entering
            const response = await authApi.enterRoom(roomName, deviceId);
            console.log('🔑 [PREJOIN] enterRoom API response:', response);
            console.log('🔑 [PREJOIN] response type:', typeof response);
            console.log('🔑 [PREJOIN] response.roomStartTime:', response?.roomStartTime);
            console.log('🔑 [PREJOIN] response.roomStartTime type:', typeof response?.roomStartTime);
            if (response?.roomStartTime) {
                console.log('✅ [PREJOIN] roomStartTime received:', response.roomStartTime);
                dispatch(setRoomStartTime(response.roomStartTime));
            }
            else {
                console.error('❌ [PREJOIN] No roomStartTime in response!');
            }
            joinConference();
        }
        catch (error) {
            //@ts-ignore
            logger.error('Room check or enter failed:', error);
            // Show error message based on status code
            let errorMessage = "خطا در اتصال به سرور. لطفاً دوباره تلاش کنید.";
            if (error.status === 404) {
                errorMessage = "این اتاق وجود ندارد. لطفاً نام اتاق را بررسی کنید.";
            }
            else if (error.status === 409) {
                errorMessage = "این اتاق قبلاً به پایان رسیده است.";
            }
            else if (error.data && error.data.message) {
                // Use API message if available for other errors
                errorMessage = error.data.message;
            }
            toast.error(errorMessage, {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                rtl: true,
            });
            setIsJoining(false);
        }
    };
    const onCancelClick = () => {
        window.location.href = "/";
    };
    const onDropdownClose = () => {
        setShowJoinByPhoneButtons(false);
    };
    const onOptionsClick = (e) => {
        e?.stopPropagation();
        setShowJoinByPhoneButtons((show) => !show);
    };
    const closeDialog = () => {
        setJoinByPhoneDialogVisiblity(false);
    };
    const doShowDialog = () => {
        setJoinByPhoneDialogVisiblity(true);
        onDropdownClose();
    };
    const showDialogKeyPress = (e) => {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            doShowDialog();
        }
    };
    const onJoinConferenceWithoutAudioKeyPress = (e) => {
        if (joinConferenceWithoutAudio && (e.key === " " || e.key === "Enter")) {
            e.preventDefault();
            //@ts-ignore
            logger.info("Prejoin joinConferenceWithoutAudio dispatched on a key pressed.");
            joinConferenceWithoutAudio();
        }
    };
    const getExtraJoinButtons = () => {
        const noAudio = {
            key: "no-audio",
            testId: "prejoin.joinWithoutAudio",
            icon: IconVolumeOff,
            label: t("prejoin.joinWithoutAudio"),
            onClick: () => {
                //@ts-ignore
                logger.info("Prejoin join conference without audio pressed.");
                joinConferenceWithoutAudio();
            },
            onKeyPress: onJoinConferenceWithoutAudioKeyPress,
        };
        const byPhone = {
            key: "by-phone",
            testId: "prejoin.joinByPhone",
            icon: IconPhoneRinging,
            label: t("prejoin.joinAudioByPhone"),
            onClick: doShowDialog,
            onKeyPress: showDialogKeyPress,
        };
        return {
            noAudio,
            byPhone,
        };
    };
    const extraJoinButtons = getExtraJoinButtons();
    let extraButtonsToRender = Object.values(extraJoinButtons).filter((val) => !(prejoinConfig?.hideExtraJoinButtons || []).includes(val.key));
    if (!hasJoinByPhoneButton) {
        extraButtonsToRender = extraButtonsToRender.filter((btn) => btn.key !== "by-phone");
    }
    const hasExtraJoinButtons = Boolean(extraButtonsToRender.length);
    if (autoJoining) {
        const loadingText = isNewMeeting
            ? t("welcomepage.creatingMeeting")
            : t("welcomepage.joiningMeeting");
        return (React.createElement("div", { className: classes.autoJoinOverlay },
            React.createElement("div", { className: classes.autoJoinSpinner }),
            React.createElement("div", { className: classes.autoJoinText }, loadingText)));
    }
    return (React.createElement("div", { className: classes.container },
        React.createElement("img", { alt: "Didar", className: classes.mobileLogo, src: "images/didar-logo.png" }),
        React.createElement("div", { className: classes.header },
            React.createElement("img", { alt: "Didar", className: classes.logo, src: "images/didar-logo.png" }),
            isUserAuthenticated && userInfo && (React.createElement("div", { className: classes.userMenu, ref: dropdownRef },
                React.createElement("button", { className: classes.userBtn, type: "button" },
                    React.createElement("div", { className: classes.userInfo },
                        React.createElement("span", { className: classes.userName }, userInfo.username),
                        React.createElement("span", { className: classes.userPhone }, userInfo.phone.replace("+98", "0"))),
                    React.createElement("div", { className: classes.userAvatar },
                        React.createElement("p", null, userInfo.username?.slice(0, 2))))))),
        isAuthChecking ? (
        /* Skeleton Loading */
        React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.skeletonMainContent },
                React.createElement("div", { className: classes.skeletonLeftPanel },
                    React.createElement("div", { className: classes.skeletonVideoWrapper },
                        React.createElement("div", { className: classes.skeletonToolbox },
                            React.createElement("div", { className: classes.skeletonToolboxContainer },
                                React.createElement("div", { style: { width: "56px" } }),
                                React.createElement("div", { className: classes.skeletonToolboxCenter },
                                    React.createElement("div", { className: classes.skeletonToolboxButton }),
                                    React.createElement("div", { className: classes.skeletonToolboxButton })),
                                React.createElement("div", { className: classes.skeletonToolboxButtonRight })))),
                    React.createElement("div", { className: classes.skeletonMobileButtons },
                        React.createElement("div", { className: classes.skeletonMobileJoinButton }),
                        React.createElement("div", { className: classes.skeletonMobileCancelButton }))),
                React.createElement("div", { className: classes.skeletonRightPanel },
                    React.createElement("div", { className: classes.skeletonTitle }),
                    React.createElement("div", { style: { width: "70%" } },
                        React.createElement("div", { className: classes.skeletonJoinButton }),
                        React.createElement("div", { className: classes.skeletonCancelButton, style: { margin: "8px auto 0" } })))))) : (React.createElement("div", { className: classes.mainContent },
            React.createElement("div", { className: classes.leftPanel },
                React.createElement("div", { className: classes.videoWrapper },
                    React.createElement(Preview, { videoMuted: !showCameraPreview, videoTrack: videoTrack }),
                    isKnocking && (React.createElement("div", { className: classes.knockingOverlay },
                        React.createElement("div", { className: classes.knockingSpinner }),
                        React.createElement("div", { className: classes.knockingText }, t("lobby.joiningMessage")))),
                    React.createElement("button", { className: classes.previewSettingsButton, onClick: handleSettingsButtonClick, title: t("toolbar.Settings"), type: "button" },
                        React.createElement(Icon, { size: 24, src: IconDotsVertical })),
                    React.createElement("div", { className: "prejoin-username-overlay" }, userDisplayName),
                    React.createElement(PrejoinAudioIndicator, { audioTrack: audioTrack }),
                    React.createElement("div", { className: classes.prejoinToolbox },
                        React.createElement("div", { className: classes.toolboxContainer },
                            React.createElement("div", { className: classes.toolboxLeft }),
                            React.createElement("div", { className: `${classes.toolboxCenter} prejoin-toolbox-center` },
                                React.createElement(AudioSettingsButton, { handleClick: () => { } }),
                                React.createElement(VideoSettingsButton, { handleClick: () => { } })),
                            React.createElement("div", { className: classes.toolboxRight },
                                React.createElement("button", { className: classes.toolboxButton, onClick: handleBackgroundClick, title: t("toolbar.selectBackground"), type: "button" },
                                    React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", fill: "#f1e9e9", viewBox: "0 0 256 256" },
                                        React.createElement("path", { d: "M48,64a8,8,0,0,1,8-8H72V40a8,8,0,0,1,16,0V56h16a8,8,0,0,1,0,16H88V88a8,8,0,0,1-16,0V72H56A8,8,0,0,1,48,64ZM184,192h-8v-8a8,8,0,0,0-16,0v8h-8a8,8,0,0,0,0,16h8v8a8,8,0,0,0,16,0v-8h8a8,8,0,0,0,0-16Zm56-48H224V128a8,8,0,0,0-16,0v16H192a8,8,0,0,0,0,16h16v16a8,8,0,0,0,16,0V160h16a8,8,0,0,0,0-16ZM219.31,80,80,219.31a16,16,0,0,1-22.62,0L36.68,198.63a16,16,0,0,1,0-22.63L176,36.69a16,16,0,0,1,22.63,0l20.68,20.68A16,16,0,0,1,219.31,80Zm-54.63,32L144,91.31l-96,96L68.68,208ZM208,68.69,187.31,48l-32,32L176,100.69Z" }))))))),
                React.createElement("div", { className: classes.mobileButtonsWrapper },
                    React.createElement("div", { className: classes.mobileJoinButton },
                        React.createElement(ActionButton, { disabled: !isUserAuthenticated ||
                                joiningInProgress ||
                                isJoining ||
                                isKnocking ||
                                (showUnsafeRoomWarning && !unsafeRoomConsent) ||
                                showErrorOnField, hasOptions: false, onClick: onJoinButtonClick, testId: "prejoin.joinMeeting.mobile", type: "primary" }, isKnocking
                            ? t("lobby.joiningTitle")
                            : t("prejoin.joinMeeting"))),
                    React.createElement("button", { className: classes.mobileCancelButton, onClick: onCancelClick, type: "button" }, t("prejoin.cancel")))),
            React.createElement("div", { className: classes.rightPanel },
                React.createElement("div", { className: classes.logoAnimation },
                    React.createElement(DotLottieReact, { autoplay: true, loop: true, src: "images/didar-logo.json" })),
                React.createElement("div", { className: classes.joinButtonWrapper },
                    React.createElement(Popover, { content: hasExtraJoinButtons && (React.createElement("div", { className: classes.dropdownButtons }, extraButtonsToRender.map(({ key, ...rest }) => (React.createElement(Button, { disabled: joiningInProgress || showErrorOnField, fullWidth: true, key: key, type: BUTTON_TYPES.SECONDARY, ...rest }))))), onPopoverClose: onDropdownClose, position: "bottom", trigger: "click", visible: showJoinByPhoneButtons },
                        React.createElement(ActionButton, { OptionsIcon: showJoinByPhoneButtons ? IconArrowUp : IconArrowDown, disabled: !isUserAuthenticated ||
                                joiningInProgress ||
                                isJoining ||
                                isKnocking ||
                                (showUnsafeRoomWarning && !unsafeRoomConsent) ||
                                showErrorOnField, hasOptions: hasExtraJoinButtons && !isKnocking, onClick: onJoinButtonClick, onOptionsClick: onOptionsClick, testId: "prejoin.joinMeeting", type: "primary" }, isKnocking
                            ? t("lobby.joiningTitle")
                            : t("prejoin.joinMeeting"))),
                    React.createElement("button", { className: classes.cancelButton, onClick: onCancelClick, type: "button" }, t("prejoin.cancel")))))),
        showDialog && (React.createElement(JoinByPhoneDialog, { joinConferenceWithoutAudio: joinConferenceWithoutAudio, onClose: closeDialog })),
        React.createElement(LoginModal, { onLoginSuccess: async () => {
                try {
                    const me = await authApi.getMe();
                    dispatch(setUserInfo(me));
                    dispatch(setAuthenticated(true));
                    setUserDisplayName(me.username || me.phone);
                    dispatchUpdateSettings({ displayName: me.username || me.phone });
                    setIsUserAuthenticated(true);
                    // Save to cookie for next time
                    authApi.saveUserInfo(me);
                }
                catch (error) {
                    console.error("Failed to get user info after login:", error);
                }
            } }),
        React.createElement(ToastContainer, null)));
};
function mapStateToProps(state) {
    const name = getDisplayName(state);
    const showErrorOnJoin = isDisplayNameRequired(state) && !name;
    const { id: participantId } = getLocalParticipant(state) ?? {};
    const { joiningInProgress } = state["features/prejoin"];
    const { room } = state["features/base/conference"];
    const { unsafeRoomConsent } = state["features/base/premeeting"];
    const { knocking } = state["features/lobby"];
    return {
        audioTrack: getLocalJitsiAudioTrack(state),
        hasJoinByPhoneButton: isJoinByPhoneButtonVisible(state),
        isDisplayNameVisible: isPrejoinDisplayNameVisible(state),
        isKnocking: knocking,
        joiningInProgress,
        name,
        participantId,
        prejoinConfig: state["features/base/config"].prejoinConfig,
        readOnlyName: isNameReadOnly(state),
        showCameraPreview: !isVideoMutedByUser(state),
        showDialog: isJoinByPhoneDialogVisible(state),
        showErrorOnJoin,
        showUnsafeRoomWarning: isInsecureRoomName(room) && isUnsafeRoomWarningEnabled(state),
        unsafeRoomConsent,
        videoTrack: getLocalJitsiVideoTrack(state),
    };
}
const mapDispatchToProps = {
    joinConferenceWithoutAudio: joinConferenceWithoutAudioAction,
    joinConference: joinConferenceAction,
    setJoinByPhoneDialogVisiblity: setJoinByPhoneDialogVisiblityAction,
    updateSettings,
};
export default connect(mapStateToProps, mapDispatchToProps)(Prejoin);
